import { Button } from "$components/button/button";
import { InfoField } from "$components/info-field";
import { useEmitNotification } from "$routes/_lib/use-emit-notification";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { useState } from "react";
import type {
  BulkUploadResultFragmentFragment,
  BulkUploadValidationFragmentFragment,
} from "./__generated__/queries.gql";
import { triggerBrowserCsvDownload } from "./browser-utils";
import { type Data, ValidationResultTable } from "./validation-result-table";

const Details: FC<{
  id: string;
  totalSent: number;
  totalAdditions: number;
  totalUpdates: number;
  totalErrors: number;
}> = ({ id, totalSent, totalAdditions, totalUpdates, totalErrors }) => {
  return (
    <Box display="flex" flexWrap="wrap" alignItems="center">
      <InfoField id={`${id}-totalSent`} label="Total Sent" value={totalSent} />
      <InfoField
        id={`${id}-totalAdditions`}
        label="Total Additions"
        value={totalAdditions}
      />
      <InfoField
        id={`${id}-totalUpdates`}
        label="Total Updates"
        value={totalUpdates}
      />
      <InfoField
        id={`${id}-totalErrors`}
        label="Total Errors"
        value={totalErrors}
      />
    </Box>
  );
};

interface Props {
  /**
   * Used to name the download file
   */
  entityName: string;
  downloadCsv?: () => Promise<{ csv: string; totalCount?: number }>;
  triggerCsvValidation?: (csv: string) => void;
  validationResult?: BulkUploadValidationFragmentFragment;
  validationResultLoading?: boolean;
  triggerCsvBulkImport?: (csv: string) => void;
  bulkImportResult?: BulkUploadResultFragmentFragment;
  bulkImportResultLoading?: boolean;
}

export const BulkOperationsPage: FC<Props> = ({
  entityName,
  downloadCsv,
  triggerCsvValidation,
  validationResult,
  validationResultLoading,
  triggerCsvBulkImport,
  bulkImportResult,
  bulkImportResultLoading,
}) => {
  const [csvFileValue, setCsvFileValue] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [isOnValidationStep, setIsOnValidationStep] = useState<boolean>(true);
  const { emitSuccessNotification } = useEmitNotification();

  async function onDownload() {
    if (downloadCsv) {
      setDownloadLoading(true);
      const { csv, totalCount } = await downloadCsv();
      setDownloadLoading(false);
      triggerBrowserCsvDownload(csv, `${entityName}-bulk-download.csv`);

      emitSuccessNotification(`Downloaded CSV with ${totalCount} entries`);
    }
  }

  async function onFileChange(evt) {
    const file = evt.target.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const contents = e.target.result as string;
      triggerCsvValidation?.(contents);
      setCsvFileValue(contents);
      setIsOnValidationStep(true);
    };
    reader.readAsText(file);
  }

  function onBulkUpload() {
    triggerCsvBulkImport?.(csvFileValue);
    setIsOnValidationStep(false);
  }

  const totalToUpdate =
    (validationResult?.totalAdditions || 0) +
    (validationResult?.totalUpdates || 0);

  const showValidationResult =
    isOnValidationStep && !validationResultLoading && csvFileValue;
  const showBulkUploadResult =
    !isOnValidationStep && !bulkImportResultLoading && bulkImportResult;

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {downloadCsv ? (
          <Button loading={downloadLoading} onClick={onDownload}>
            Download CSV
          </Button>
        ) : (
          <div />
        )}
        <Box display="flex" alignItems="center">
          {csvFileValue &&
            totalToUpdate > 0 &&
            validationResult?.totalErrors === 0 && (
              <Button loading={bulkImportResultLoading} onClick={onBulkUpload}>
                Save
              </Button>
            )}
          <Box marginLeft={2} maxWidth="350px">
            <Typography>Select a file to upload here:</Typography>
            <Box marginTop={1} marginBottom={1}>
              <input
                data-testid="csv-file-upload-input"
                type="file"
                accept=".csv"
                onChange={onFileChange}
              />
            </Box>
            <Typography variant="caption">
              <em>
                You will be able to see the validation results first before you
                actually process the upload
              </em>
            </Typography>
            {validationResult?.totalErrors > 0 && (
              <Box marginTop={1}>
                <Typography color="error">
                  Fix all the errors and try again
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {showBulkUploadResult && (
        <Box marginTop={3}>
          <Typography variant="h4">Upload details:</Typography>
          <Box marginTop={2} marginBottom={2}>
            <Details id="upload" {...bulkImportResult} />
          </Box>
          <ul>
            {bulkImportResult?.errors?.map(
              ({ id, unflattenedData, errorMessage }) => (
                <li>
                  <Tooltip title={JSON.stringify(unflattenedData, null, 2)}>
                    <span>
                      {id} - {errorMessage}
                    </span>
                  </Tooltip>
                </li>
              ),
            )}
          </ul>
        </Box>
      )}

      {showValidationResult && (
        <>
          <Box marginTop={3}>
            <Typography variant="h4">Validation Details:</Typography>
            <Box marginTop={2}>
              <Details id="validation" {...validationResult} />
            </Box>
          </Box>
          <Box marginTop={3}>
            {validationResult?.items?.length > 0 && (
              <ValidationResultTable
                data={validationResult.items as Data}
                loading={validationResultLoading}
                storageKey={`bulkOperations.${entityName}.pageSize`}
              />
            )}
          </Box>
        </>
      )}
    </div>
  );
};
